window.July4Fireworks = (function () {
  const STORAGE_KEY = 'july4-fireworks-disabled';
  const COLORS = ['#e63946', '#ffffff', '#1d3557', '#2563eb', '#ff4d6d', '#93c5fd'];
  const SHOW_DURATION_MS = 18000;
  const LAUNCH_INTERVAL_MS = 400;

  let activeSession = null;

  class Particle {
    constructor(x, y, color, velocity, decay, gravity) {
      this.x = x;
      this.y = y;
      this.color = color;
      this.velocity = velocity;
      this.alpha = 1;
      this.decay = decay;
      this.gravity = gravity;
      this.size = 2.5 + Math.random() * 2.5;
    }

    update() {
      this.velocity.y += this.gravity;
      this.x += this.velocity.x;
      this.y += this.velocity.y;
      this.alpha -= this.decay;
    }

    draw(ctx) {
      if (this.alpha <= 0) return;

      ctx.save();
      ctx.globalAlpha = this.alpha;
      ctx.fillStyle = this.color;
      ctx.shadowBlur = 12;
      ctx.shadowColor = this.color;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  class Rocket {
    constructor(x, targetY) {
      this.x = x;
      this.y = window.innerHeight;
      this.targetY = targetY;
      this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
      this.velocity = {
        x: (Math.random() - 0.5) * 2.5,
        y: -(11 + Math.random() * 5),
      };
      this.exploded = false;
      this.particles = [];
    }

    explode() {
      this.exploded = true;
      const particleCount = 70 + Math.floor(Math.random() * 40);

      for (let i = 0; i < particleCount; i++) {
        const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.15;
        const speed = 2.2 + Math.random() * 4.8;

        this.particles.push(
          new Particle(
            this.x,
            this.y,
            this.color,
            {
              x: Math.cos(angle) * speed,
              y: Math.sin(angle) * speed,
            },
            0.006 + Math.random() * 0.01,
            0.035 + Math.random() * 0.02
          )
        );
      }
    }

    update() {
      if (!this.exploded) {
        this.x += this.velocity.x;
        this.y += this.velocity.y;

        if (this.y <= this.targetY) {
          this.explode();
        }
      } else {
        this.particles.forEach((particle) => particle.update());
        this.particles = this.particles.filter((particle) => particle.alpha > 0);
      }
    }

    draw(ctx) {
      if (!this.exploded) {
        ctx.save();
        ctx.fillStyle = this.color;
        ctx.shadowBlur = 10;
        ctx.shadowColor = this.color;
        ctx.globalAlpha = 0.95;
        ctx.beginPath();
        ctx.arc(this.x, this.y, 3, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = 0.45;
        ctx.beginPath();
        ctx.arc(this.x - this.velocity.x, this.y + 12, 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        return;
      }

      this.particles.forEach((particle) => particle.draw(ctx));
    }

    isDone() {
      return this.exploded && this.particles.length === 0;
    }
  }

  function isDisabled() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === 'true';
    } catch (error) {
      return false;
    }
  }

  function setDisabled(disabled) {
    try {
      window.localStorage.setItem(STORAGE_KEY, disabled ? 'true' : 'false');
    } catch (error) {
      // Ignore storage errors (private browsing, etc.)
    }

    updateToggle();
  }

  function updateToggle() {
    const toggle = document.getElementById('july-4-fireworks-toggle');
    if (!toggle) return;

    const disabled = isDisabled();
    toggle.setAttribute('aria-pressed', disabled ? 'false' : 'true');
    toggle.classList.toggle('july-4-fireworks-toggle--off', disabled);
    toggle.querySelector('.july-4-fireworks-toggle__label').textContent = disabled
      ? 'Fireworks off'
      : 'Fireworks on';
  }

  function stop() {
    if (!activeSession) return;

    const { container, animationFrame, launchTimer, resize, ctx, canvas } = activeSession;

    cancelAnimationFrame(animationFrame);
    clearTimeout(launchTimer);
    window.removeEventListener('resize', resize);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    container.classList.remove('july-4-fireworks--active');
    container.classList.add('july-4-fireworks--done');
    delete container.dataset.started;

    activeSession = null;
  }

  function start() {
    const canvas = document.getElementById('july-4-fireworks');
    const container = document.getElementById('july-4-fireworks-container');

    if (!canvas || !container) return;
    if (isDisabled()) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (container.dataset.started === 'true') return;

    container.dataset.started = 'true';
    container.classList.remove('july-4-fireworks--done');

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rockets = [];
    const startTime = Date.now();

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const session = {
      container,
      animationFrame: null,
      launchTimer: null,
      resize,
      ctx,
      canvas,
    };
    activeSession = session;

    const cleanup = () => {
      cancelAnimationFrame(session.animationFrame);
      clearTimeout(session.launchTimer);
      window.removeEventListener('resize', resize);
      container.classList.remove('july-4-fireworks--active');
      container.classList.add('july-4-fireworks--done');
      delete container.dataset.started;
      activeSession = null;
    };

    const launch = () => {
      if (isDisabled() || Date.now() - startTime > SHOW_DURATION_MS) return;

      const x = Math.random() * canvas.width * 0.75 + canvas.width * 0.125;
      const targetY = Math.random() * canvas.height * 0.4 + canvas.height * 0.06;
      rockets.push(new Rocket(x, targetY));
      session.launchTimer = window.setTimeout(launch, LAUNCH_INTERVAL_MS + Math.random() * 300);
    };

    const animate = () => {
      if (isDisabled()) {
        cleanup();
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = rockets.length - 1; i >= 0; i--) {
        rockets[i].update();
        rockets[i].draw(ctx);

        if (rockets[i].isDone()) {
          rockets.splice(i, 1);
        }
      }

      const elapsed = Date.now() - startTime;
      if (elapsed < SHOW_DURATION_MS + 3000 || rockets.length > 0) {
        session.animationFrame = requestAnimationFrame(animate);
      } else {
        cleanup();
      }
    };

    resize();
    window.addEventListener('resize', resize);
    container.classList.add('july-4-fireworks--active');
    launch();
    animate();
  }

  function initToggle() {
    const toggle = document.getElementById('july-4-fireworks-toggle');
    if (!toggle) return;

    updateToggle();

    toggle.addEventListener('click', () => {
      const disabled = !isDisabled();
      setDisabled(disabled);

      if (disabled) {
        stop();
      } else {
        start();
      }
    });
  }

  return { start, stop, initToggle, isDisabled };
})();
